import { Music, Image as ImageIcon, X } from 'lucide-react';
import FileInput from '../ui/FileInput';
import { renderRichText } from '../../lib/richText';
import type { QuestionBlock } from '@eps/shared-types';

// Matches QuestionStemFields' existing palette (mirrors apps/mobile MockTestScreen styles).
const BROWN = '#2D2016';
const PANEL_BG = '#FFF9E8';
const PANEL_BORDER = '#F5E6D0';

interface QuestionBlockEditorProps {
  block: Exclude<QuestionBlock, { type: 'container' }>;
  onChange: (block: QuestionBlock) => void;
  onDelete: () => void;
}

export default function QuestionBlockEditor({
  block,
  onChange,
  onDelete,
}: QuestionBlockEditorProps) {
  return (
    <div className="relative rounded-lg border p-2" style={{ borderColor: PANEL_BORDER }}>
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-1.5 top-1.5 text-gray-300 hover:text-gray-500"
        title="Remove block"
      >
        <X size={14} />
      </button>

      {block.type === 'text' && (
        <textarea
          value={block.text}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder="Text"
          rows={2}
          data-rt-field={`block:${block.id}`}
          className="w-full resize-none rounded-md border-0 bg-transparent pr-5 py-1 font-bold leading-snug outline-none transition-colors focus:bg-white/60"
          style={{ color: BROWN, fontSize: 15 }}
        />
      )}

      {block.type === 'text' && /[biu]>/.test(block.text) && (
        <div
          className="mt-1 rounded-md bg-white/60 px-2 py-1 text-sm font-korean"
          style={{ color: BROWN }}
        >
          <span className="mr-2 text-[10px] uppercase tracking-wide text-gray-400">Preview</span>
          {renderRichText(block.text)}
        </div>
      )}

      {block.type === 'image' && (
        <div className="space-y-1.5 pr-5">
          <div
            className="flex items-center justify-center overflow-hidden rounded-[10px] border-2 border-dashed"
            style={{
              width: 160,
              height: 130,
              backgroundColor: PANEL_BG,
              borderColor: PANEL_BORDER,
            }}
          >
            {block.imageUrl ? (
              <img src={block.imageUrl} alt="" className="h-full w-full object-contain" />
            ) : (
              <ImageIcon size={20} className="text-gray-300" />
            )}
          </div>
          <FileInput
            value={block.imageUrl}
            onChange={(imageUrl) => onChange({ ...block, imageUrl })}
            accept="image"
            placeholder="Select image"
          />
        </div>
      )}

      {block.type === 'audio' && (
        <div className="space-y-1.5 pr-5">
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2"
            style={{ backgroundColor: PANEL_BG }}
          >
            <Music size={14} color="#4A3728" />
            <span className="text-xs font-semibold" style={{ color: '#4A3728' }}>
              {block.audioUrl ? 'Audio attached' : 'No audio yet'}
            </span>
          </div>
          <FileInput
            value={block.audioUrl}
            onChange={(audioUrl) => onChange({ ...block, audioUrl })}
            accept="audio"
            placeholder="Select audio"
          />
        </div>
      )}
    </div>
  );
}
